/**
 * All dashboard UI strings (English / Swedish). Use dot paths, e.g. t('nav.inbox').
 */

function deepGet(obj, path) {
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[p];
  }
  return cur;
}

export function translate(locale, path, vars) {
  const dict = locale === 'sv' ? sv : en;
  let s = deepGet(dict, path);
  if (typeof s !== 'string') {
    s = deepGet(en, path);
  }
  if (typeof s !== 'string') return path;
  if (vars && typeof s === 'string') {
    Object.keys(vars).forEach((k) => {
      s = s.split(`{${k}}`).join(String(vars[k]));
    });
  }
  return s;
}

export function normalizeDashboardLocale(value) {
  const v = String(value || '')
    .trim()
    .toLowerCase();
  if (v === 'sv' || v === 'swe' || v === 'sv-se' || v.startsWith('sv')) return 'sv';
  if (v === 'en' || v === 'eng' || v === 'en-us' || v.startsWith('en')) return 'en';
  return null;
}

const en = {
  shell: {
    workspaceLabel: 'Workspace',
    language: 'Language',
    langEnglish: 'English',
    langSwedish: 'Swedish',
    loadingWorkspace: 'Loading your workspace…',
    searchPlaceholder: 'Search…',
    logOut: 'Log out',
    composeAria: 'Compose',
    mainNavAria: 'Main navigation',
    workspaceNavAria: 'Workspace',
    botControlBadge:
      'Bot-controlled navigation — set REACT_APP_DASHBOARD_BOT_ONLY_NAV=0 for clickable menu',
    tryAgain: 'Try again',
    supportChatAria: 'Support chat',
  },
  time: {
    justNow: 'just now',
    minutesAgo: '{n}m ago',
    hoursAgo: '{n}h ago',
    daysAgo: '{n}d ago',
  },
  nav: {
    inboxConversations: 'Inbox & Conversations',
    inbox: 'My Inbox',
    conversations: 'Conversations',
    allConversations: 'All Conversations',
    mentions: 'Mentions',
    unattended: 'Unattended',
    channels: 'Channels',
    labels: 'Labels',
    bonnie: 'Bonnie',
    faqs: 'FAQs',
    documents: 'Documents',
    scenarios: 'Scenarios',
    playground: 'Playground',
    bonnieInboxes: 'Inboxes',
    tools: 'Tools',
    contacts: 'Contacts',
    reports: 'Reports',
    overview: 'Overview',
    campaigns: 'Campaigns',
    liveChat: 'Live Chat',
    allCampaigns: 'All campaigns',
    helpCenter: 'Help Center',
    articles: 'Articles',
    categories: 'Categories',
    locales: 'Locales',
    settings: 'Settings',
    accountSettings: 'Account Settings',
    agents: 'Agents',
    teams: 'Teams',
    settingsInboxes: 'Inboxes',
    automation: 'Automation',
    cannedResponses: 'Canned Responses',
    integrations: 'Integrations',
    auditLogs: 'Audit Logs',
    fallbackChannelChat: 'Klippkant',
    fallbackChannelMail: 'Support-mail',
  },
  common: {
    refresh: 'Refresh',
    chat: 'Chat',
    enabled: 'Enabled',
    disabled: 'Disabled',
    type: 'Type',
    general: 'General',
    nothingYet: 'Nothing here yet',
    seedHint: 'Open this page again after sign-in to seed workspace data.',
    workspaceFallback: 'Workspace',
    more: 'More',
  },
  conversations: {
    title: 'Conversations',
    mentions: 'Mentions',
    unattended: 'Unattended',
    mine: 'Mine',
    unassigned: 'Unassigned',
    all: 'All',
    empty: 'No conversations in this view. Data is loaded from your workspace in PostgreSQL.',
    inView: '{count} conversation in view',
    inViewPlural: '{count} conversations in view',
    selectPrompt: 'Please select a conversation from left pane',
    shortcutCmdK: '⌘ K to open command menu',
    shortcutCmdSlash: '⌘ / to view keyboard shortcuts',
    roleAgent: 'Agent',
    roleContact: 'Contact',
  },
  inbox: {
    title: 'My Inbox',
    emptyTitle: 'No notifications',
    emptyBody: 'Notifications from your workspace are stored in PostgreSQL and appear here when present.',
  },
  contacts: {
    title: 'Contacts',
    message: 'Message',
    viewDetails: 'View details',
    emptyTitle: 'No contacts',
    emptyBody: 'Contacts are seeded per account when the list is empty.',
    showing: 'Showing {from} - {to} of {total} contacts',
    pageOf: '{current} of {total} page',
  },
  campaigns: {
    title: 'Campaigns',
    create: '+ Create campaign',
    emptyTitle: 'No campaigns',
    emptyBody: 'Campaigns are created automatically when your list is empty.',
  },
  liveChat: {
    title: 'Live chat campaigns',
    emptyTitle: 'No live chat campaigns',
    emptyIntro: 'Add a campaign with type',
    emptyOutro: 'in the database or use seeded demo data.',
    footer: 'Live chat · from workspace data',
  },
  bonnie: {
    subtitleFallback: 'Assistant',
    faqsTitle: 'FAQs',
    createFaq: '+ Create new FAQ',
    emptyFaqs: 'No FAQs',
    emptyFaqsBody: 'FAQs are loaded from PostgreSQL for your account.',
    showingItems: 'Showing {from} - {to} of {total} items',
    documentsTitle: 'Documents',
    documentsSubtitle: 'Knowledge base entries from your workspace',
    documentsEmpty: 'No documents',
    documentsEmptyBody: 'Documents are stored as workspace notes with page_key document.',
    scenariosTitle: 'Scenarios',
    scenariosSubtitle: 'Conversation and flow scenarios',
    scenariosEmpty: 'No scenarios',
    playgroundTitle: 'Playground',
    playgroundSubtitle: 'Test prompts before publishing',
    playgroundEmpty: 'No playground notes',
    inboxesTitle: 'Inboxes',
    inboxesSubtitle: 'Bonnie inbox configuration notes',
    inboxesEmpty: 'No inbox notes',
    toolsTitle: 'Tools',
    toolsSubtitle: 'Connected tools and integrations (Bonnie)',
    toolsEmpty: 'No tools',
  },
  help: {
    articlesTitle: 'Articles',
    articlesSubtitle: 'Help center content from PostgreSQL',
    articlesEmpty: 'No articles',
    articlesEmptyBody: 'Articles are seeded when your help library is empty.',
    categoriesTitle: 'Categories',
    categoriesSubtitle: 'Derived from help articles in your workspace',
    categoriesEmpty: 'No categories',
    categoriesEmptyBody: 'Add help articles with a category to see them listed here.',
    localesTitle: 'Locales',
    localesSubtitle: 'Languages present in help articles',
    localesEmpty: 'No locales',
    localesEmptyBody: 'Article locale codes appear here (e.g. en, sv).',
  },
  settingsPages: {
    agentsTitle: 'Agents',
    agentsSubtitle: 'Team members from your workspace database',
    agentsEmpty: 'No agents',
    agentsEmptyBody: 'Agents are seeded when the table is empty for your account.',
    teamsTitle: 'Teams',
    teamsSubtitle: 'Routing groups stored in PostgreSQL',
    teamsEmpty: 'No teams',
    teamsEmptyBody: 'Teams are created when your list is empty.',
    inboxesTitle: 'Inboxes',
    inboxesSubtitle: 'Mail and chat channels for your workspace',
    inboxesEmpty: 'No inboxes',
    inboxesEmptyBody: 'Inboxes are seeded from dashboard_mail_channels.',
    labelsTitle: 'Labels',
    labelsSubtitle: 'Navigation labels (dashboard_nav_labels)',
    labelsEmpty: 'No labels',
    labelsEmptyBody: 'Labels are seeded when your label list is empty.',
    automationTitle: 'Automation',
    automationSubtitle: 'Rules and automations',
    automationEmpty: 'No automation rules',
    cannedTitle: 'Canned Responses',
    cannedSubtitle: 'Saved replies from workspace notes',
    cannedEmpty: 'No canned responses',
    integrationsTitle: 'Integrations',
    integrationsSubtitle: 'Connected services',
    integrationsEmpty: 'No integrations',
    auditTitle: 'Audit Logs',
    auditSubtitle: 'Workspace activity snapshots',
    auditEmpty: 'No audit entries',
  },
  reports: {
    overviewTitle: 'Overview',
    overviewSubtitleNamed: 'Welcome back, {name} — demo metrics at a glance',
    overviewSubtitle: 'Demo metrics at a glance',
    conversationsTitle: 'Conversations',
    conversationsSubtitleNamed: 'Insights for {name} — conversation analytics',
    conversationsSubtitle: 'Conversation analytics and top questions',
    agentsTitle: 'Agents',
    agentsSubtitle: 'Agent status from your workspace',
    agentsEmpty: 'No agents to display',
    agentsEmptyBody: 'Open Settings → Agents or reload after sign-in to seed workspace data.',
  },
  dashboard: {
    activeDemos: 'Active Demos',
    totalUsers: 'Total Users',
    conversionRate: 'Conversion Rate',
    avgResponse: 'Avg. Response Time',
    fromLastWeek: 'from last week',
    fromLastMonth: 'from last month',
    faster: 'faster',
    platformFeatures: 'Platform Features',
    usage: '{pct}% usage',
    recentSessions: 'Recent Demo Sessions',
    colUser: 'User',
    colProduct: 'Product',
    colDuration: 'Duration',
    colStatus: 'Status',
    filterByStatus: 'Filter sessions by status',
    filterStatusAll: 'All statuses',
    sessionsNoMatch: 'No sessions match this filter.',
    sessionsEmpty: 'No demo sessions yet.',
  },
  analytics: {
    vsLastPeriod: 'vs last period',
    mostAsked: 'Most Asked Questions',
    times: '{n} times',
    perfOverTime: 'Performance Over Time',
    chartHint: 'Connect a chart library (e.g. Recharts) here — your metrics already live in PostgreSQL.',
  },
  settings: {
    title: 'Account settings',
    signedInAs: 'Signed in as {name}',
    general: 'General settings',
    savedOk: 'Settings saved to PostgreSQL.',
    saveFailed: 'Save failed',
    saving: 'Saving…',
    update: 'Update settings',
    botSection: 'Bot configuration',
    botName: 'Bot name',
    greeting: 'Greeting message',
    responseSpeed: 'Response speed',
    speedFast: 'Fast (0.5–1s)',
    speedNormal: 'Normal (1–2s)',
    speedSlow: 'Slow (2–3s)',
    workspaceSection: 'Workspace',
    accountName: 'Account / workspace name',
    siteLanguage: 'Site language',
    siteLanguagePh: 'e.g. en, sv',
    incomingDomain: 'Incoming email domain',
    supportEmail: 'Support email',
    featuresSection: 'Features',
    voiceTitle: 'Voice interaction',
    voiceHint: 'Voice input and output',
    screenTitle: 'Screen sharing',
    screenHint: 'Screen share during demos',
  },
  login: {
    checking: 'Checking session…',
    titleLogin: 'Login to Custio',
    titleRegister: 'Create your Custio account',
    sso: 'Login via SSO',
    ssoTitle: 'Single sign-on is not configured for this environment.',
    or: 'OR',
    email: 'Email',
    emailPh: 'example@companyname.com',
    password: 'Password',
    forgot: 'Forgot your password?',
    forgotTitle: 'Password reset is not available in this demo.',
    passwordPh: 'Password',
    showPw: 'Show password',
    hidePw: 'Hide password',
    hintRegister: 'Use at least 8 characters for your password.',
    genericErr: 'Something went wrong',
    wait: 'Please wait…',
    loginBtn: 'Login',
    createAccountBtn: 'Create account',
    newTo: 'New to Custio?',
    createAccountLink: 'Create an account',
    haveAccount: 'Already have an account?',
    loginLink: 'Login',
    lobbyHintBefore: 'Wrong dashboard account?',
    lobbyHintLink: 'Open the live demo lobby',
  },
  app: {
    loading: 'Loading…',
  },
  placeholder: {
    wireApi: 'Wire your API or embed the full Custio experience here.',
    demoDefault: 'This section is a demo placeholder.',
  },
};

const sv = {
  shell: {
    workspaceLabel: 'Arbetsyta',
    language: 'Språk',
    langEnglish: 'Engelska',
    langSwedish: 'Svenska',
    loadingWorkspace: 'Laddar din arbetsyta…',
    searchPlaceholder: 'Sök…',
    logOut: 'Logga ut',
    composeAria: 'Skriv nytt',
    mainNavAria: 'Huvudnavigering',
    workspaceNavAria: 'Arbetsyta',
    botControlBadge:
      'Botstyrd navigering — sätt REACT_APP_DASHBOARD_BOT_ONLY_NAV=0 för klickbar meny',
    tryAgain: 'Försök igen',
    supportChatAria: 'Supportchatt',
  },
  time: {
    justNow: 'nyss',
    minutesAgo: '{n} min sedan',
    hoursAgo: '{n} tim sedan',
    daysAgo: '{n} dagar sedan',
  },
  nav: {
    inboxConversations: 'Inkorg och konversationer',
    inbox: 'Min inkorg',
    conversations: 'Konversationer',
    allConversations: 'Alla konversationer',
    mentions: 'Omnämningar',
    unattended: 'Obesvarade',
    channels: 'Kanaler',
    labels: 'Etiketter',
    bonnie: 'Bonnie',
    faqs: 'Vanliga frågor',
    documents: 'Dokument',
    scenarios: 'Scenarier',
    playground: 'Testmiljö',
    bonnieInboxes: 'Inkorgar',
    tools: 'Verktyg',
    contacts: 'Kontakter',
    reports: 'Rapporter',
    overview: 'Översikt',
    campaigns: 'Kampanjer',
    liveChat: 'Livechatt',
    allCampaigns: 'Alla kampanjer',
    helpCenter: 'Hjälpcenter',
    articles: 'Artiklar',
    categories: 'Kategorier',
    locales: 'Språkversioner',
    settings: 'Inställningar',
    accountSettings: 'Kontoinställningar',
    agents: 'Agenter',
    teams: 'Team',
    settingsInboxes: 'Inkorgar',
    automation: 'Automation',
    cannedResponses: 'Standardsvar',
    integrations: 'Integrationer',
    auditLogs: 'Aktivitetsloggar',
    fallbackChannelChat: 'Klippkant',
    fallbackChannelMail: 'Support-mail',
  },
  common: {
    refresh: 'Uppdatera',
    chat: 'Chatt',
    enabled: 'Aktiverad',
    disabled: 'Inaktiverad',
    type: 'Typ',
    general: 'Allmänt',
    nothingYet: 'Inget här ännu',
    seedHint: 'Öppna sidan igen efter inloggning för att fylla exempeldata.',
    workspaceFallback: 'Arbetsyta',
    more: 'Mer',
  },
  conversations: {
    title: 'Konversationer',
    mentions: 'Omnämningar',
    unattended: 'Obesvarade',
    mine: 'Mina',
    unassigned: 'Otilldelade',
    all: 'Alla',
    empty: 'Inga konversationer i den här vyn. Data hämtas från din arbetsyta i PostgreSQL.',
    inView: '{count} konversation i vyn',
    inViewPlural: '{count} konversationer i vyn',
    selectPrompt: 'Välj en konversation i vänster panel',
    shortcutCmdK: 'K för kommandomeny',
    shortcutCmdSlash: '/ för kortkommandon',
    roleAgent: 'Agent',
    roleContact: 'Kontakt',
  },
  inbox: {
    title: 'Min inkorg',
    emptyTitle: 'Inga aviseringar',
    emptyBody: 'Aviseringar från din arbetsyta sparas i PostgreSQL och visas här.',
  },
  contacts: {
    title: 'Kontakter',
    message: 'Meddelande',
    viewDetails: 'Visa detaljer',
    emptyTitle: 'Inga kontakter',
    emptyBody: 'Kontakter skapas automatiskt när listan är tom.',
    showing: 'Visar {from} - {to} av {total} kontakter',
    pageOf: 'Sida {current} av {total}',
  },
  campaigns: {
    title: 'Kampanjer',
    create: '+ Skapa kampanj',
    emptyTitle: 'Inga kampanjer',
    emptyBody: 'Kampanjer skapas automatiskt när listan är tom.',
  },
  liveChat: {
    title: 'Livechatt-kampanjer',
    emptyTitle: 'Inga livechatt-kampanjer',
    emptyIntro: 'Lägg till en kampanj med typ',
    emptyOutro: 'i databasen eller använd exempeldata.',
    footer: 'Livechatt · från arbetsytedata',
  },
  bonnie: {
    subtitleFallback: 'Assistent',
    faqsTitle: 'Vanliga frågor',
    createFaq: '+ Skapa ny FAQ',
    emptyFaqs: 'Inga vanliga frågor',
    emptyFaqsBody: 'FAQ:er hämtas från PostgreSQL för ditt konto.',
    showingItems: 'Visar {from} - {to} av {total} poster',
    documentsTitle: 'Dokument',
    documentsSubtitle: 'Kunskapsbasposter från din arbetsyta',
    documentsEmpty: 'Inga dokument',
    documentsEmptyBody: 'Dokument sparas som arbetsytanteckningar med page_key document.',
    scenariosTitle: 'Scenarier',
    scenariosSubtitle: 'Konversations- och flödesscenarier',
    scenariosEmpty: 'Inga scenarier',
    playgroundTitle: 'Testmiljö',
    playgroundSubtitle: 'Testa promptar innan publicering',
    playgroundEmpty: 'Inga anteckningar i testmiljön',
    inboxesTitle: 'Inkorgar',
    inboxesSubtitle: 'Bonnie-inkorgsanteckningar',
    inboxesEmpty: 'Inga inkorgsanteckningar',
    toolsTitle: 'Verktyg',
    toolsSubtitle: 'Anslutna verktyg och integrationer (Bonnie)',
    toolsEmpty: 'Inga verktyg',
  },
  help: {
    articlesTitle: 'Artiklar',
    articlesSubtitle: 'Hjälpcenterinnehåll från PostgreSQL',
    articlesEmpty: 'Inga artiklar',
    articlesEmptyBody: 'Artiklar skapas när ditt hjäpbibliotek är tomt.',
    categoriesTitle: 'Kategorier',
    categoriesSubtitle: 'Härleds från hjälpartiklar i din arbetsyta',
    categoriesEmpty: 'Inga kategorier',
    categoriesEmptyBody: 'Lägg till hjälpartiklar med kategori för att se dem här.',
    localesTitle: 'Språkversioner',
    localesSubtitle: 'Språk som finns i hjälpartiklar',
    localesEmpty: 'Inga språkversioner',
    localesEmptyBody: 'Artikelns språkkoder visas här (t.ex. en, sv).',
  },
  settingsPages: {
    agentsTitle: 'Agenter',
    agentsSubtitle: 'Teammedlemmar från din databas',
    agentsEmpty: 'Inga agenter',
    agentsEmptyBody: 'Agenter skapas när tabellen är tom för ditt konto.',
    teamsTitle: 'Team',
    teamsSubtitle: 'Routinggrupper i PostgreSQL',
    teamsEmpty: 'Inga team',
    teamsEmptyBody: 'Team skapas när listan är tom.',
    inboxesTitle: 'Inkorgar',
    inboxesSubtitle: 'E-post- och chattkanaler för din arbetsyta',
    inboxesEmpty: 'Inga inkorgar',
    inboxesEmptyBody: 'Inkorgar skapas från dashboard_mail_channels.',
    labelsTitle: 'Etiketter',
    labelsSubtitle: 'Navigationsetiketter (dashboard_nav_labels)',
    labelsEmpty: 'Inga etiketter',
    labelsEmptyBody: 'Etiketter skapas när listan är tom.',
    automationTitle: 'Automation',
    automationSubtitle: 'Regler och automationer',
    automationEmpty: 'Inga automatiseringsregler',
    cannedTitle: 'Standardsvar',
    cannedSubtitle: 'Sparade svar från arbetsytanteckningar',
    cannedEmpty: 'Inga standardsvar',
    integrationsTitle: 'Integrationer',
    integrationsSubtitle: 'Anslutna tjänster',
    integrationsEmpty: 'Inga integrationer',
    auditTitle: 'Aktivitetsloggar',
    auditSubtitle: 'Aktivitetsögonblicksbilder',
    auditEmpty: 'Inga loggposter',
  },
  reports: {
    overviewTitle: 'Översikt',
    overviewSubtitleNamed: 'Välkommen tillbaka, {name} — demoöversikt',
    overviewSubtitle: 'Demoöversikt',
    conversationsTitle: 'Konversationer',
    conversationsSubtitleNamed: 'Insikter för {name} — konversationsanalys',
    conversationsSubtitle: 'Konversationsanalys och vanligaste frågor',
    agentsTitle: 'Agenter',
    agentsSubtitle: 'Agentstatus från din arbetsyta',
    agentsEmpty: 'Inga agenter att visa',
    agentsEmptyBody: 'Öppna Inställningar → Agenter eller ladda om efter inloggning.',
  },
  dashboard: {
    activeDemos: 'Aktiva demos',
    totalUsers: 'Totalt antal användare',
    conversionRate: 'Konverteringsgrad',
    avgResponse: 'Snitt svarstid',
    fromLastWeek: 'jämfört med förra veckan',
    fromLastMonth: 'jämfört med förra månaden',
    faster: 'snabbare',
    platformFeatures: 'Plattformsfunktioner',
    usage: '{pct}% användning',
    recentSessions: 'Senaste demosessioner',
    colUser: 'Användare',
    colProduct: 'Produkt',
    colDuration: 'Längd',
    colStatus: 'Status',
    filterByStatus: 'Filtrera sessioner efter status',
    filterStatusAll: 'Alla statusar',
    sessionsNoMatch: 'Inga sessioner matchar filtret.',
    sessionsEmpty: 'Inga demosessioner ännu.',
  },
  analytics: {
    vsLastPeriod: 'jämfört med förra perioden',
    mostAsked: 'Mest ställda frågor',
    times: '{n} gånger',
    perfOverTime: 'Prestanda över tid',
    chartHint:
      'Anslut ett diagrambibliotek (t.ex. Recharts) här — dina mätvärden finns redan i PostgreSQL.',
  },
  settings: {
    title: 'Kontoinställningar',
    signedInAs: 'Inloggad som {name}',
    general: 'Allmänna inställningar',
    savedOk: 'Inställningar sparade i PostgreSQL.',
    saveFailed: 'Kunde inte spara',
    saving: 'Sparar…',
    update: 'Uppdatera inställningar',
    botSection: 'Botkonfiguration',
    botName: 'Botnamn',
    greeting: 'Hälsningsmeddelande',
    responseSpeed: 'Svarshastighet',
    speedFast: 'Snabb (0,5–1 s)',
    speedNormal: 'Normal (1–2 s)',
    speedSlow: 'Långsam (2–3 s)',
    workspaceSection: 'Arbetsyta',
    accountName: 'Konto- / arbetsytans namn',
    siteLanguage: 'Webbplatsens språk',
    siteLanguagePh: 't.ex. en, sv',
    incomingDomain: 'Domän för inkommande e-post',
    supportEmail: 'Support-e-post',
    featuresSection: 'Funktioner',
    voiceTitle: 'Röstinteraktion',
    voiceHint: 'Röstinmatning och uppspelning',
    screenTitle: 'Skärmdelning',
    screenHint: 'Skärmdelning under demos',
  },
  login: {
    checking: 'Kontrollerar session…',
    titleLogin: 'Logga in på Custio',
    titleRegister: 'Skapa ditt Custio-konto',
    sso: 'Logga in med SSO',
    ssoTitle: 'Enkel inloggning är inte konfigurerad i den här miljön.',
    or: 'ELLER',
    email: 'E-post',
    emailPh: 'exempel@foretag.se',
    password: 'Lösenord',
    forgot: 'Glömt lösenord?',
    forgotTitle: 'Återställning av lösenord finns inte i den här demon.',
    passwordPh: 'Lösenord',
    showPw: 'Visa lösenord',
    hidePw: 'Dölj lösenord',
    hintRegister: 'Använd minst 8 tecken i lösenordet.',
    genericErr: 'Något gick fel',
    wait: 'Vänta…',
    loginBtn: 'Logga in',
    createAccountBtn: 'Skapa konto',
    newTo: 'Ny hos Custio?',
    createAccountLink: 'Skapa ett konto',
    haveAccount: 'Har du redan ett konto?',
    loginLink: 'Logga in',
    lobbyHintBefore: 'Fel dashboardkonto?',
    lobbyHintLink: 'Öppna live demo-lobbyn',
  },
  app: {
    loading: 'Laddar…',
  },
  placeholder: {
    wireApi: 'Koppla ditt API eller bädda in hela Custio-upplevelsen här.',
    demoDefault: 'Det här avsnittet är en demoplatshållare.',
  },
};
